import { startOfDay } from "date-fns";
import React, { createContext, useEffect, useState } from "react";
import { getExtras, getSlots, getTrips, validateVoucher } from "../../api/bookingApi";
import { BookTrip, defaultBookTrip } from "../../api/DomainModel/BookTrip";
import { Extra } from "../../api/DomainModel/Extra";
import { Slot } from "../../api/DomainModel/Slot";
import { Trip } from "../../api/DomainModel/Trip";
import { presentationTrips } from "../Trips/trips-data";
import { TripContextType } from "./TripContextType";

export const TripContext = createContext<TripContextType>({} as TripContextType);

type BookingContextProviderProps = {
  children: React.ReactNode;
};

const BookingContextProvider = ({ children }: BookingContextProviderProps) => {
  const [selectedTrip, setSelectedTrip] = useState<Trip>();
  const [booking, setBooking] = useState<BookTrip>(defaultBookTrip);
  const [selectedSlot, setSelectedSlot] = useState<Slot>();
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [extras, setExtras] = useState<Extra[]>([]);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [voucherDiscount, setVoucherDiscount] = useState(0);
  const [voucherTitle, setVoucherTitle] = useState("");
  const [voucherStatus, setVoucherStatus] = useState<"idle" | "loading" | "valid" | "invalid">("idle");

  const selectTrip = (tripId: string) => {
    let trip = trips.find((x) => x.id === tripId);

    if (trip) {
      setSelectedTrip({ ...trip });

      setLoadingSlots(true);
      setBooking((x) => ({
        ...x,
        ignoreDiscounts: trip.ignoreDiscounts,
      }));

      getExtras(trip.id).then((response) => {
        setExtras(response);
        getSlots(trip.id).then((x) => {
          setSlots(x.data);
          setLoadingSlots(false);

          const presentationTrip = presentationTrips.find((x) => x.tripObjectId === tripId);

          if (presentationTrip) {
            if (presentationTrip.defaultDate === "today") {
              const today = new Date();
              selectDate(today, x.data);
            } else {
              selectDate(new Date(presentationTrip.defaultDate), x.data);
            }
          }
        });
      });
    }
  };

  const selectDate = (date: Date, availableSlots?: Slot[]) => {
    setSelectedDate(date);

    const slotsToUse = availableSlots || slots;
    const daySlots = slotsToUse.filter(
      (x) => startOfDay(new Date(x.slotDate.startDate)).toString() === startOfDay(date).toString(),
    );

    // A day can hold several recurring series, so skip the ones whose departures have already passed.
    const slot = daySlots.find((x) => x.departureOriginTimes.length > 0) ?? daySlots[0];

    setSelectedSlot(slot);

    if (slot) {
      setBooking((prev) => ({ ...prev, slotId: slot.id }));
    }
  };

  const selectExtra = (extraItem: Extra) => {
    setBooking((prev) => {
      let extras = [...prev.extras];

      let extraFound = prev.extras.find((x) => x.extraId === extraItem.extraId);

      if (extraFound) {
        if (extraItem.amount > 0) {
          extras = extras.map((x) => (x.extraId === extraItem.extraId ? { ...extraItem } : x));
        } else {
          extras = extras.filter((x) => x.extraId !== extraItem.extraId);
        }
      } else {
        if (extraItem.amount > 0) {
          extras = [...prev.extras, extraItem];
        }
      }

      return { ...prev, extras: extras };
    });
  };

  const clearVoucher = () => {
    setVoucherDiscount(0);
    setVoucherTitle("");
    setVoucherStatus("idle");
    setBooking((prev) => ({ ...prev, voucherCode: undefined }));
  };

  const applyVoucher = async (code: string) => {
    if (!code.trim()) return;
    setVoucherStatus("loading");
    try {
      const result = await validateVoucher(code.trim(), booking.email?.trim() || undefined);
      if (result.isValid) {
        setVoucherDiscount(result.percentage);
        setVoucherTitle(result.title ?? "");
        setVoucherStatus("valid");
        setBooking((prev) => ({ ...prev, voucherCode: code.trim(), applyResidentsDiscount: false }));
      } else {
        setVoucherStatus("invalid");
      }
    } catch {
      setVoucherStatus("invalid");
    }
  };

  const applyResidentsDiscount = (apply: boolean) => {
    if (apply) {
      setVoucherDiscount(0);
      setVoucherTitle("");
      setVoucherStatus("idle");
      setBooking((prev) => ({ ...prev, applyResidentsDiscount: true, voucherCode: undefined }));
    } else {
      setBooking((prev) => ({ ...prev, applyResidentsDiscount: false }));
    }
  };

  const setNumberOfAdults = (value: number) => {
    setBooking((prev) => ({ ...prev, numberOfAdults: value }));
  };

  const setNumberOfChildren = (value: number) => {
    setBooking((prev) => ({ ...prev, numberOfChildren: value }));
  };

  const increaseNumberOfChildren = () => {
    setBooking((prev) => ({
      ...prev,
      numberOfChildren: prev.numberOfChildren + 1,
    }));
  };
  const decreaseNumberOfChildren = () => {
    setBooking((prev) => ({
      ...prev,
      numberOfChildren: prev.numberOfChildren > 0 ? prev.numberOfChildren - 1 : 0,
    }));
  };
  const increaseNumberOfAdults = () => {
    setBooking((prev) => ({
      ...prev,
      numberOfAdults: prev.numberOfAdults + 1,
    }));
  };
  const decreaseNumberOfAdults = () => {
    setBooking((prev) => ({
      ...prev,
      numberOfAdults: prev.numberOfAdults > 0 ? prev.numberOfAdults - 1 : 0,
    }));
  };

  useEffect(() => {
    getTrips()
      .then((x) => {
        setTrips(x.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  return (
    <TripContext.Provider
      value={{
        trips,
        selectedTrip,
        selectTrip,
        booking,
        setBooking,
        selectedSlot,
        setSelectedSlot,
        extras,
        slots,
        loadingSlots,
        selectDate,
        selectedDate,
        selectExtra,
        applyResidentsDiscount,
        setNumberOfAdults,
        setNumberOfChildren,
        increaseNumberOfChildren,
        decreaseNumberOfChildren,
        increaseNumberOfAdults,
        decreaseNumberOfAdults,
        voucherDiscount,
        voucherTitle,
        voucherStatus,
        applyVoucher,
        clearVoucher,
      }}
    >
      {children}
    </TripContext.Provider>
  );
};

export default BookingContextProvider;
